import axios from 'axios';
import { fetchAuthSession } from 'aws-amplify/auth';
import { sanitizeDeep } from '../utils/sanitize';
import { storage } from '../utils/storage';
import { mergePromoAttribution, clearPromoAttribution } from '../utils/promoAttribution';
import { FEATURE_GATE_ERROR_CODE, openFeatureGate } from '../config/featureGates';
import { handleSubscriptionExpiredError } from '../utils/subscriptionStatus';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

// Función para obtener el organizationId dinámicamente
const getOrganizationId = () => {
  // Primero intentar obtener del localStorage (usuario autenticado)
  const organizationId = storage.get('organizationId');
  if (organizationId) {
    return organizationId;
  }

  // Fallback a la variable de entorno para desarrollo/testing
  const fallbackId = process.env.REACT_APP_RESTAURANT_ID || 'rest-001';
  console.warn(`No se encontró organizationId en localStorage. Usando fallback: ${fallbackId}`);
  return fallbackId;
};

// Función para obtener el restaurantId seleccionado
const getRestaurantId = () => {
  // Intentar obtener del localStorage (restaurante seleccionado)
  const restaurantId = storage.get('selectedRestaurantId');
  if (restaurantId) {
    return restaurantId;
  }

  // Fallback a la variable de entorno para desarrollo/testing
  const fallbackId = process.env.REACT_APP_RESTAURANT_ID || 'rest-001';
  console.warn(
    `No se encontró selectedRestaurantId en localStorage. Usando fallback: ${fallbackId}`
  );
  return fallbackId;
};

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Interceptor para agregar el token de autenticación a todas las peticiones
api.interceptors.request.use(
  async (config) => {
    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();

      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }

      const selectedOrgId = storage.get('selectedOrganizationId');
      const selectedRestaurantId = storage.get('selectedRestaurantId');

      if (selectedOrgId) {
        config.headers['X-Organization-Id'] = selectedOrgId;
      }

      // Un request puede apuntar a otro local de la cadena (p. ej. movimientos por local, RES-41)
      if (selectedRestaurantId && !config.headers['X-Restaurant-Id']) {
        config.headers['X-Restaurant-Id'] = selectedRestaurantId;
      }
    } catch (error) {
      console.error('[API Interceptor] Error obteniendo token:', error.message);
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Interceptor para sanitizar inputs HTML en requests de escritura
api.interceptors.request.use(
  (config) => {
    const method = (config.method || '').toLowerCase();
    if (
      ['post', 'put', 'patch'].includes(method) &&
      config.data &&
      typeof config.data === 'object'
    ) {
      config.data = sanitizeDeep(config.data);
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Interceptor para manejar errores de autenticación
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Feature-gating (NER-3161): el backend rechaza features fuera del plan con
    // 403 FEATURE_GATE_REJECTED. Disparamos el modal de upgrade/add-on globalmente
    // y dejamos que el error siga propagándose (el caller decide si además muestra
    // un fallback). El FeatureGateHost escucha este evento y abre el modal correcto.
    if (error.response?.status === 403 && error.response?.data?.error === FEATURE_GATE_ERROR_CODE) {
      openFeatureGate(error.response.data);
    }

    // Paywall (fin de trial sin pago): 402 SUBSCRIPTION_EXPIRED (o 403 con el
    // mismo code). Dispara el bloqueo una sola vez (dedupe en subscriptionStatus);
    // SubscriptionContext lo escucha y el route guard manda a /subscription/expired.
    handleSubscriptionExpiredError(error);

    if (error.response?.status === 401) {
      // No redirigir en endpoints que manejan 401/404 como parte de su flujo normal
      const url = error.config?.url || '';
      const isOrganizationEndpoint = url.includes('/organizations');

      if (!isOrganizationEndpoint) {
        // Si recibimos un 401 en otros endpoints, redirigir a login
        console.error('No autorizado, redirigiendo a login...');
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Restaurant
export const listRestaurants = async () => {
  const response = await api.get('/restaurants');
  return response.data;
};

export const getRestaurant = async (restaurantId) => {
  const response = await api.get(`/restaurants/${restaurantId}`);
  return response.data;
};

export const createRestaurant = async (data) => {
  const response = await api.post('/restaurants', data);
  return response.data;
};

export const updateRestaurant = async (restaurantId, data) => {
  const organizationId = restaurantId || getOrganizationId();
  const response = await api.put(`/restaurants/${organizationId}`, data);
  return response.data;
};

export const deleteRestaurant = async (restaurantId) => {
  const response = await api.delete(`/restaurants/${restaurantId}`);
  return response.data;
};

// Order modes / delivery settings (NER-1237)
// PATCH /restaurants/{restaurantId}/settings — body { orderModes, deliverySettings, restaurantLocation, ownDelivery }
// 403 con header X-Required-Plan si el plan no incluye `delivery`.
export const updateRestaurantSettings = async (restaurantId, settings) => {
  const response = await api.patch(`/restaurants/${restaurantId}/settings`, settings);
  return response.data;
};

// Repartidores propios (own delivery)
export const getRiders = async (restaurantId) => {
  const response = await api.get(`/restaurants/${restaurantId}/riders`);
  return response.data;
};

export const createRider = async (restaurantId, data) => {
  const response = await api.post(`/restaurants/${restaurantId}/riders`, data);
  return response.data;
};

export const updateRider = async (restaurantId, riderId, data) => {
  const response = await api.patch(`/restaurants/${restaurantId}/riders/${riderId}`, data);
  return response.data;
};

export const deleteRider = async (restaurantId, riderId) => {
  const response = await api.delete(`/restaurants/${restaurantId}/riders/${riderId}`);
  return response.data;
};

// Menu
// RES-54: la carta del panel sale del endpoint autenticado. El público
// (`/menu/restaurants/{id}`, sin authorizer, el que consume la app del cliente) ya no devuelve
// receta, costo, margen, stock ni vínculo de reventa: con solo mandar el header
// `X-Organization-Id` cualquiera se llevaba el costo y el margen de toda la carta.
export const getMenu = async () => {
  const restaurantId = getRestaurantId();
  const response = await api.get(`/menu/restaurants/${restaurantId}/admin`);
  return response.data;
};

export const createCategory = async (data) => {
  const restaurantId = getRestaurantId();
  const response = await api.post(`/menu/restaurants/${restaurantId}/categories`, data);
  return response.data;
};

export const createItem = async (data) => {
  const restaurantId = getRestaurantId();
  const response = await api.post(`/menu/restaurants/${restaurantId}/items`, data);
  return response.data;
};

export const updateCategory = async (categoryId, data) => {
  const restaurantId = getRestaurantId();
  const response = await api.put(
    `/menu/restaurants/${restaurantId}/categories/${categoryId}`,
    data
  );
  return response.data;
};

export const updateItem = async (itemId, data) => {
  const restaurantId = getRestaurantId();
  const response = await api.put(`/menu/restaurants/${restaurantId}/items/${itemId}`, data);
  return response.data;
};

export const deleteCategory = async (categoryId) => {
  const restaurantId = getRestaurantId();
  const response = await api.delete(`/menu/restaurants/${restaurantId}/categories/${categoryId}`);
  return response.data;
};

export const deleteItem = async (itemId) => {
  const restaurantId = getRestaurantId();
  const response = await api.delete(`/menu/restaurants/${restaurantId}/items/${itemId}`);
  return response.data;
};

export const toggleItemAvailability = async (itemId) => {
  const restaurantId = getRestaurantId();
  const response = await api.patch(
    `/menu/restaurants/${restaurantId}/items/${itemId}/availability`
  );
  return response.data;
};

export const getUploadUrl = async (resourceType, resourceId, contentType) => {
  const restaurantId = getRestaurantId();
  // Convert plural to singular for backend
  const type = resourceType === 'categories' ? 'category' : 'item';
  const response = await api.post(`/menu/restaurants/${restaurantId}/upload-url`, {
    type,
    id: resourceId,
    contentType,
  });
  return response.data;
};

// Tables
export const getTables = async () => {
  const restaurantId = getRestaurantId();
  const response = await api.get(`/tables/restaurants/${restaurantId}`);
  return response.data;
};

export const createTable = async (data) => {
  const restaurantId = getRestaurantId();
  const response = await api.post(`/tables/restaurants/${restaurantId}`, data);
  return response.data;
};

export const updateTable = async (tableNumber, data) => {
  const restaurantId = getRestaurantId();
  const response = await api.put(`/tables/restaurants/${restaurantId}/${tableNumber}`, data);
  return response.data;
};

export const deleteTable = async (tableNumber) => {
  const restaurantId = getRestaurantId();
  const response = await api.delete(`/tables/restaurants/${restaurantId}/${tableNumber}`);
  return response.data;
};

export const generateQR = async (tableNumber) => {
  const restaurantId = getRestaurantId();
  const response = await api.post(`/tables/restaurants/${restaurantId}/${tableNumber}/qr`);
  return response.data;
};

// Table Groups
export const getGroups = async () => {
  const restaurantId = getRestaurantId();
  const response = await api.get(`/tables/restaurants/${restaurantId}/groups`);
  return response.data;
};

export const createGroup = async ({ name, tableNumbers }) => {
  const restaurantId = getRestaurantId();
  const response = await api.post(`/tables/restaurants/${restaurantId}/groups`, {
    name,
    tableNumbers,
  });
  return response.data;
};

export const updateGroup = async (groupId, data) => {
  const restaurantId = getRestaurantId();
  const response = await api.put(`/tables/restaurants/${restaurantId}/groups/${groupId}`, data);
  return response.data;
};

export const removeGroup = async (groupId) => {
  const restaurantId = getRestaurantId();
  const response = await api.delete(`/tables/restaurants/${restaurantId}/groups/${groupId}`);
  return response.data;
};

export const closeSession = async (sessionId, forceClose = false) => {
  const response = await api.post(`/sessions/${sessionId}/close`, { forceClose });
  return response.data;
};

// Devuelve los pedidos de una sesión con totales (totalSession/totalPaid/totalUnpaid).
// Usado para advertir de pedidos impagos antes de cerrar una mesa.
export const getSessionOrders = async (sessionId) => {
  const response = await api.get(`/orders/sessions/${sessionId}`);
  return response.data;
};

// Orders
export const getOrders = async (options = {}) => {
  const restaurantId = getRestaurantId();
  const params = new URLSearchParams();

  // Soportar parámetros individuales o objeto de opciones
  const { status, date, startDate, endDate, limit } = options;

  if (status) params.append('status', status);
  if (date) params.append('date', date);
  if (startDate) params.append('startDate', startDate);
  if (endDate) params.append('endDate', endDate);
  if (limit) params.append('limit', limit);

  const response = await api.get(`/orders/restaurants/${restaurantId}?${params.toString()}`);
  return response.data;
};

// Alias for invoicing module compatibility
export const getOrdersByRestaurant = (restaurantId) => getOrders();

// Update order (status, discount, etc.)
export const updateOrder = async (orderId, updates) => {
  const restaurantId = getRestaurantId();
  const response = await api.put(`/orders/${orderId}?restaurantId=${restaurantId}`, updates);
  return response.data;
};

export const updateOrderStatus = async (orderId, status, date) => {
  const restaurantId = getRestaurantId();
  // Backend expone PATCH /orders/{orderId}/status y lee restaurantId/date del body
  const response = await api.patch(`/orders/${orderId}/status`, {
    status,
    restaurantId,
    date: date || new Date().toISOString().split('T')[0],
  });
  return response.data;
};

// Stats - Estadísticas de ventas
export const getStats = async (options = {}) => {
  const restaurantId = getRestaurantId();
  const params = new URLSearchParams();

  const { period = 'today', startDate, endDate } = options;

  params.append('period', period);
  if (startDate) params.append('startDate', startDate);
  if (endDate) params.append('endDate', endDate);

  const response = await api.get(`/orders/restaurants/${restaurantId}/stats?${params.toString()}`);
  return response.data;
};

// Organizations
export const createOrganization = async (data) => {
  const response = await api.post('/organizations', data);
  return response.data;
};

// ── NER-3781: flujo Google admin (services/social-auth, NER-3785) ────────────
// En este flujo el usuario todavía NO tiene sesión Cognito, así que usamos
// `axios` "crudo" en vez del cliente `api`: (1) el interceptor de request de
// `api` intentaría adjuntar un idToken inexistente, y (2) su interceptor de
// response redirige a /login ante un 401 — pero acá un authToken inválido debe
// mostrarse como error en CreatingOrgPage, no rebotar al login.

/**
 * Re-verifica server-side el authToken firmado (HMAC) que Google admin-callback
 * devolvió en el #fragment del returnUrl, antes de crear la organización.
 * @param {string} authToken
 * @returns {Promise<{email:string, given_name:string, name:string, emailVerified:boolean, flow:string}>}
 * @throws si el token es inválido/expirado/manipulado (401).
 */
export const verifySignedToken = async (authToken) => {
  const response = await axios.post(`${API_URL}/auth/verify-signed-token`, { authToken });
  return response.data;
};

/**
 * Hallazgo QA 2026-09-10 (ronda 3): chequea si un slug de restaurante ya está
 * en uso ANTES de crear el restaurante, usando el mismo endpoint público que
 * ya usa el customer-app (`GET /restaurants/public/resolve-slug-global`).
 * Espeja la validación de unicidad global que hace `createRestaurant` server-
 * side (NER-1931) — así el wizard (email Step 2 y gate de Google) puede avisar
 * "nombre ya usado" ANTES de enviar el formulario, en vez de que la creación
 * best-effort post-signup falle en silencio con un 409 (Diego, 2026-09-10).
 *
 * Público / sin auth: se llama antes de tener sesión Cognito en ambos flujos.
 *
 * @param {string} slug
 * @returns {Promise<boolean>} true si el slug está libre.
 */
export const checkSlugAvailability = async (slug) => {
  const response = await axios.get(`${API_URL}/restaurants/public/resolve-slug-global`, {
    params: { slug },
    validateStatus: (status) => status === 200 || status === 404 || status === 409,
  });
  // 404 = ningún restaurante activo con ese slug -> libre.
  // 200/409 = ya existe (o colisión ambigua) -> ocupado.
  return response.status === 404;
};

/**
 * Completa el alta del flujo Google admin (NER-3807): dado el `authToken` HMAC
 * verificado y el nombre de organización elegido por el usuario en el gate de
 * confirmación, el backend crea el usuario Cognito (email ya verificado por
 * Google) + la organización, y devuelve tokens Cognito para establecer sesión.
 *
 * Usa `axios` "crudo" (no el cliente `api`) por las mismas razones que
 * `verifySignedToken`: todavía no hay sesión Cognito y no queremos que el
 * interceptor de response rebote a /login ante un 4xx — cada status tiene un
 * ruteo específico (ver CreatingOrgPage.handleConfirm). Por eso `validateStatus`
 * acepta 2xx–4xx y el caller inspecciona `status`/`data`; solo los 5xx lanzan.
 *
 * @param {string} authToken        JWT HMAC del #fragment de admin-callback.
 * @param {string} organizationName Nombre elegido por el usuario.
 * @returns {Promise<import('axios').AxiosResponse>} respuesta cruda (status + data).
 */
export const completeGoogleSignup = async (authToken, organizationName, cuit) => {
  return axios.post(
    `${API_URL}/social/complete-signup`,
    { authToken, organizationName, ...(cuit ? { cuit } : {}) },
    { validateStatus: (status) => status >= 200 && status < 500 }
  );
};

/**
 * NER-4160: inicia sesión de un usuario Google YA EXISTENTE (login, no signup).
 *
 * Un usuario que se dio de alta con Google no conoce su contraseña Cognito (el
 * backend la genera aleatoria en completeSocialSignup), así que no puede usar el
 * form email/password. Dado el `authToken` HMAC verificado que admin-callback
 * devolvió con `existing=1`, el backend re-emite una sesión Cognito para ese
 * email SIN crear organización, y devuelve los tokens para establecer sesión.
 *
 * Mismo criterio de `axios` "crudo" + `validateStatus` que `completeGoogleSignup`:
 * no hay sesión Cognito todavía y cada status tiene ruteo propio en
 * CreatingOrgPage (201 → sesión; 401 → token inválido; 404 → no existe → registro).
 *
 * @param {string} authToken JWT HMAC del #fragment de admin-callback.
 * @returns {Promise<import('axios').AxiosResponse>} respuesta cruda (status + data).
 */
export const completeGoogleLogin = async (authToken) => {
  return axios.post(
    `${API_URL}/social/complete-login`,
    { authToken },
    { validateStatus: (status) => status >= 200 && status < 500 }
  );
};

export const getMyOrganization = async () => {
  const response = await api.get('/organizations/me');
  return response.data;
};

export const updateOrganization = async (organizationId, data) => {
  const response = await api.put(`/organizations/${organizationId}`, data);
  return response.data;
};

export const inviteUser = async (organizationId, data) => {
  const response = await api.post(`/organizations/${organizationId}/invite`, data);
  return response.data;
};

export const listOrganizationUsers = async (organizationId) => {
  const response = await api.get(`/organizations/${organizationId}/users`);
  return response.data;
};

// Multi-tenancy: Obtener restaurantes del usuario en una organización
export const listMyRestaurants = async (organizationId) => {
  const response = await api.get(`/organizations/${organizationId}/my-restaurants`);
  return response.data;
};

export const editUser = async (organizationId, userId, data) => {
  const response = await api.put(`/organizations/${organizationId}/users/${userId}`, data);
  return response.data;
};

/**
 * Fija o resetea el PIN de comanda de un usuario (RES-111). El servidor sube `pinVersion`
 * (invalida los tokens de operador vivos) y levanta el bloqueo por intentos.
 */
export const setUserPin = async (organizationId, userId, pin) => {
  const response = await api.put(`/organizations/${organizationId}/users/${userId}/pin`, { pin });
  return response.data;
};

export const generateAvatarUploadUrl = async (organizationId, userId, contentType) => {
  const response = await api.post(
    `/organizations/${organizationId}/users/${userId}/avatar-upload-url`,
    {
      contentType,
    }
  );
  return response.data;
};

export const toggleUserStatus = async (organizationId, userId, currentStatus) => {
  const newStatus = currentStatus === 'active' ? 'inactive' : 'active';
  const response = await api.patch(`/organizations/${organizationId}/users/${userId}/status`, {
    status: newStatus,
  });
  return response.data;
};

// Eliminar usuario de la organización (borrado lógico)
export const deleteUser = async (organizationId, userId) => {
  const response = await api.delete(`/organizations/${organizationId}/users/${userId}`);
  return response.data;
};

// Obtener usuarios con acceso a un restaurante
export const getRestaurantUsers = async (organizationId, restaurantId) => {
  const response = await api.get(
    `/organizations/${organizationId}/restaurants/${restaurantId}/users`
  );
  return response.data;
};

// Asignar usuarios a un restaurante
export const assignUsersToRestaurant = async (organizationId, restaurantId, userIds) => {
  const response = await api.put(
    `/organizations/${organizationId}/restaurants/${restaurantId}/users`,
    { userIds }
  );
  return response.data;
};

// ========================================
// Cash Registers - Control de Caja
// ========================================

// Get all cash registers for a restaurant
export const getCashRegisters = async () => {
  const restaurantId = getRestaurantId();
  const response = await api.get(`/cash-registers?restaurantId=${restaurantId}`);
  return response.data;
};

// Create a new cash register
export const createCashRegister = async (data) => {
  const restaurantId = getRestaurantId();
  const response = await api.post('/cash-registers', {
    ...data,
    restaurantId,
  });
  return response.data;
};

// Update cash register
export const updateCashRegister = async (registerId, data) => {
  const restaurantId = getRestaurantId();
  const response = await api.put(`/cash-registers/${registerId}`, {
    ...data,
    restaurantId,
  });
  return response.data;
};

// Open a cash shift
export const openCashShift = async (registerId, openingAmount) => {
  const restaurantId = getRestaurantId();
  const response = await api.post(`/cash-registers/${registerId}/shifts/open`, {
    openingAmount,
    restaurantId,
  });
  return response.data;
};

// Close a cash shift
export const closeCashShift = async (
  registerId,
  shiftId,
  closingAmount,
  notes = null,
  cashCount = null
) => {
  const restaurantId = getRestaurantId();
  const response = await api.post(`/cash-registers/${registerId}/shifts/${shiftId}/close`, {
    closingAmount,
    notes,
    restaurantId,
    cashCount,
  });
  return response.data;
};

// Get shifts for a register
export const getCashShifts = async (registerId, filters = {}) => {
  const restaurantId = getRestaurantId();
  const params = new URLSearchParams({ restaurantId });

  if (filters.status) params.append('status', filters.status);
  if (filters.date) params.append('date', filters.date);

  const response = await api.get(`/cash-registers/${registerId}/shifts?${params.toString()}`);
  return response.data;
};

// Get a single shift
export const getCashShift = async (registerId, shiftId) => {
  const restaurantId = getRestaurantId();
  const response = await api.get(
    `/cash-registers/${registerId}/shifts/${shiftId}?restaurantId=${restaurantId}`
  );
  return response.data;
};

// Create a cash transaction (IN/OUT)
export const createCashTransaction = async (registerId, shiftId, data) => {
  const restaurantId = getRestaurantId();
  const response = await api.post(`/cash-registers/${registerId}/shifts/${shiftId}/transactions`, {
    ...data,
    restaurantId,
  });
  return response.data;
};

// Get transactions for a shift
export const getCashTransactions = async (registerId, shiftId) => {
  const restaurantId = getRestaurantId();
  const response = await api.get(
    `/cash-registers/${registerId}/shifts/${shiftId}/transactions?restaurantId=${restaurantId}`
  );
  return response.data;
};

// ========================================
// Customers - Base de Datos de Clientes
// ========================================

export const getCustomers = async (search = '') => {
  const restaurantId = getRestaurantId();
  const params = new URLSearchParams({ restaurantId });
  if (search) params.append('search', search);

  const response = await api.get(`/customers?${params.toString()}`);
  return response.data;
};

export const getCustomer = async (customerId) => {
  const restaurantId = getRestaurantId();
  const response = await api.get(`/customers/${customerId}?restaurantId=${restaurantId}`);
  return response.data;
};

export const createCustomer = async (data) => {
  const restaurantId = getRestaurantId();
  const response = await api.post('/customers', {
    ...data,
    restaurantId,
  });
  return response.data;
};

export const updateCustomer = async (customerId, data) => {
  const restaurantId = getRestaurantId();
  const response = await api.put(`/customers/${customerId}`, {
    ...data,
    restaurantId,
  });
  return response.data;
};

export const deleteCustomer = async (customerId) => {
  const restaurantId = getRestaurantId();
  const response = await api.delete(`/customers/${customerId}?restaurantId=${restaurantId}`);
  return response.data;
};

export const getCustomerTransactions = async (customerId) => {
  const restaurantId = getRestaurantId();
  const response = await api.get(
    `/customers/${customerId}/transactions?restaurantId=${restaurantId}`
  );
  return response.data;
};

export const addCustomerTransaction = async (customerId, data) => {
  const restaurantId = getRestaurantId();
  const response = await api.post(`/customers/${customerId}/transactions`, {
    ...data,
    restaurantId,
  });
  return response.data;
};

// ========================================
// Inventory & Expenses
// ========================================

// Inventory
export const getInventoryItems = async (params = {}) => {
  // includeInactive: los insumos desactivados no se ofrecen en recetas ni en el wizard (RES-22)
  const query = params.includeInactive ? '?includeInactive=true' : '';
  const response = await api.get(`/inventory${query}`);
  return response.data;
};

export const createInventoryItem = async (data) => {
  const response = await api.post('/inventory', data);
  return response.data;
};

export const updateInventoryItem = async (itemId, data) => {
  const response = await api.put(`/inventory/${itemId}`, data);
  return response.data;
};

export const deleteInventoryItem = async (itemId) => {
  const response = await api.delete(`/inventory/${itemId}`);
  return response.data;
};

// Catálogo de insumos a nivel organización (RES-22). El stock no se edita acá: se mueve con
// movimientos (A2 lo lleva a la fila del restaurante).
export const getInventoryCatalog = async (params = {}) => {
  const urlParams = new URLSearchParams();
  if (params.includeInactive) urlParams.append('includeInactive', 'true');
  if (params.kind) urlParams.append('kind', params.kind);
  const query = urlParams.toString();
  const response = await api.get(`/inventory/catalog${query ? `?${query}` : ''}`);
  return response.data;
};

export const createCatalogItem = async (data) => {
  const response = await api.post('/inventory/catalog', data);
  return response.data;
};

export const updateCatalogItem = async (itemId, data) => {
  const response = await api.put(`/inventory/catalog/${itemId}`, data);
  return response.data;
};

export const deleteCatalogItem = async (itemId) => {
  const response = await api.delete(`/inventory/catalog/${itemId}`);
  return response.data;
};

// Stock por restaurante (RES-23): catálogo + stock del X-Restaurant-Id; solo el mínimo del local se
// edita acá (el stock se mueve con movimientos).
export const getRestaurantStock = async (params = {}) => {
  const query = params.includeInactive ? '?includeInactive=true' : '';
  const response = await api.get(`/inventory/stock${query}`);
  return response.data;
};

export const updateRestaurantStock = async (itemId, data) => {
  const response = await api.put(`/inventory/stock/${itemId}`, data);
  return response.data;
};

// Políticas de inventario de la organización con herencia por local (RES-26).
// El GET devuelve defaults, lo de la organización, el override del local y lo efectivo.
export const getInventorySettings = async () => {
  const response = await api.get('/inventory/settings');
  return response.data;
};

export const updateInventorySettings = async (data) => {
  const response = await api.put('/inventory/settings', data);
  return response.data;
};

// Override del local: hoy solo `lowStockAlert` (null vuelve a heredar).
export const updateRestaurantInventorySettings = async (data) => {
  const response = await api.put('/inventory/settings/restaurant', data);
  return response.data;
};

// Inventory Movements
// RES-24: movimientos del restaurante seleccionado (X-Restaurant-Id), paginados por token.
// Devuelve { items, count, nextToken, restaurantId } (el backend anterior devolvía el array).
export const getInventoryMovements = async (params = {}, { restaurantId } = {}) => {
  const urlParams = new URLSearchParams();
  const keys = [
    'itemId',
    'type',
    'refType',
    'refId', // movimientos de un pedido (RES-15) u orden de compra
    'from',
    'to',
    'startDate',
    'endDate',
    'limit',
    'nextToken',
  ];
  keys.forEach((key) => {
    if (params[key] !== undefined && params[key] !== null && params[key] !== '') {
      urlParams.append(key, params[key]);
    }
  });

  const response = await api.get(`/inventory/movements?${urlParams.toString()}`, {
    ...(restaurantId ? { headers: { 'X-Restaurant-Id': restaurantId } } : {}),
  });
  return response.data;
};

export const createInventoryMovement = async (data) => {
  const response = await api.post('/inventory/movements', data);
  return response.data;
};
// Consumos de pedidos (B6 / RES-34): estado del descuento de cada pedido y reproceso
export const getOrderConsumptions = async (params = {}) => {
  const urlParams = new URLSearchParams();
  ['status', 'restaurantId', 'limit', 'orderId'].forEach((key) => {
    if (params[key] !== undefined && params[key] !== null && params[key] !== '') {
      urlParams.append(key, params[key]);
    }
  });
  const query = urlParams.toString();
  const response = await api.get(`/inventory/consumptions${query ? `?${query}` : ''}`);
  return response.data;
};

export const retryOrderConsumption = async (orderId) => {
  const response = await api.post(`/inventory/consumptions/${orderId}/retry`);
  return response.data;
};

// Wizard de inventario (RES-38): alta masiva idempotente de catálogo, stock inicial por local,
// políticas y productos postergados. Devuelve { ok, itemIds, created, reused, stock, errors }.
export const setupInventory = async (data) => {
  const response = await api.post('/inventory/setup', data);
  return response.data;
};

// Cobertura de la carta (RES-39): productos del local que todavía no descuentan stock.
export const getInventoryCoverage = async () => {
  const response = await api.get('/inventory/coverage');
  return response.data;
};

// Conteos guiados (RES-40)
export const createInventoryCount = async (data) => {
  const response = await api.post('/inventory/counts', data);
  return response.data;
};

export const getInventoryCounts = async (params = {}) => {
  const urlParams = new URLSearchParams();
  ['limit', 'nextToken'].forEach((key) => {
    if (params[key] !== undefined && params[key] !== null && params[key] !== '') {
      urlParams.append(key, params[key]);
    }
  });
  const query = urlParams.toString();
  const response = await api.get(`/inventory/counts${query ? `?${query}` : ''}`);
  return response.data;
};

export const getInventoryCount = async (countId) => {
  const response = await api.get(`/inventory/counts/${countId}`);
  return response.data;
};

// Épica D (cadenas, RES-43…47)
/** Query string con solo los parámetros que vinieron. */
const toQuery = (params = {}, keys = Object.keys(params)) => {
  const urlParams = new URLSearchParams();
  keys.forEach((key) => {
    if (params[key] !== undefined && params[key] !== null && params[key] !== '') {
      urlParams.append(key, params[key]);
    }
  });
  const query = urlParams.toString();
  return query ? `?${query}` : '';
};

// Consolidado insumo × local (RES-43): { restaurants, items, summary, categories, count }
export const getConsolidatedStock = async (params = {}) => {
  const query = toQuery(params, [
    'category',
    'status',
    'search',
    'sort',
    'itemId',
    'includeInactive',
  ]);
  const response = await api.get(`/inventory/stock/consolidated${query}`);
  return response.data;
};

// Transferencias entre locales (RES-44)
// data: { fromRestaurantId, toRestaurantId, items: [{ itemId, quantity }], reason? }
export const createInventoryTransfer = async (data) => {
  const response = await api.post('/inventory/transfers', data);
  return response.data;
};

export const getInventoryTransfers = async (params = {}) => {
  const query = toQuery(params, ['restaurantId', 'itemId', 'from', 'to', 'limit']);
  const response = await api.get(`/inventory/transfers${query}`);
  return response.data;
};

// Locales para armar una transferencia: todos los de la org con `managed` (RES-104)
export const getTransferRestaurants = async () => {
  const response = await api.get('/inventory/transfers/restaurants');
  return response.data;
};

export const getInventoryTransfer = async (transferId) => {
  const response = await api.get(`/inventory/transfers/${transferId}`);
  return response.data;
};

// Recetas estándar de la organización (RES-45). `recipeKey` = nombre normalizado.
export const getOrgRecipes = async () => {
  const response = await api.get('/inventory/org-recipes');
  return response.data;
};

export const getOrgRecipe = async (recipeKey) => {
  const response = await api.get(`/inventory/org-recipes/${encodeURIComponent(recipeKey)}`);
  return response.data;
};

// data: { itemName, recipe: [{ inventoryItemId, quantity, unit, isCritical? }], expectedVersion?, apply? }
export const saveOrgRecipe = async (data) => {
  const response = await api.put('/inventory/org-recipes', data);
  return response.data;
};

export const applyOrgRecipe = async (recipeKey) => {
  const response = await api.post(`/inventory/org-recipes/${encodeURIComponent(recipeKey)}/apply`);
  return response.data;
};

export const deleteOrgRecipe = async (recipeKey) => {
  const response = await api.delete(`/inventory/org-recipes/${encodeURIComponent(recipeKey)}`);
  return response.data;
};

// Sugerencias de insumos desde sucursales (RES-46)
// data: { name, unit, category?, kind?, reason? }
export const createCatalogSuggestion = async (data) => {
  const response = await api.post('/inventory/catalog/suggestions', data);
  return response.data;
};

export const getCatalogSuggestions = async (params = {}) => {
  const query = toQuery(params, ['status', 'restaurantId']);
  const response = await api.get(`/inventory/catalog/suggestions${query}`);
  return response.data;
};

// data: { decision: 'approve', item? } | { decision: 'reject', rejectionReason }
export const reviewCatalogSuggestion = async (suggestionId, data) => {
  const response = await api.post(`/inventory/catalog/suggestions/${suggestionId}/review`, data);
  return response.data;
};

// Reportes de inventario (RES-47). report ∈ consumption | cogs | variance | waste;
// restaurantId = 'all' pide el consolidado de la cadena.
export const getInventoryReport = async (report, params = {}) => {
  const query = toQuery(params, ['from', 'to', 'restaurantId', 'groupBy', 'includeUncounted']);
  const response = await api.get(`/inventory/reports/${report}${query}`);
  return response.data;
};
// End Inventory Movements

// Expenses
export const getExpenses = async () => {
  const response = await api.get('/expenses');
  return response.data;
};

export const createExpense = async (data) => {
  const response = await api.post('/expenses', data);
  return response.data;
};

// ========================================
// Suppliers
// ========================================

export const getSuppliers = async () => {
  const response = await api.get('/suppliers');
  return response.data;
};

export const createSupplier = async (data) => {
  const response = await api.post('/suppliers', data);
  return response.data;
};

export const updateSupplier = async (supplierId, data) => {
  const response = await api.put(`/suppliers/${supplierId}`, data);
  return response.data;
};

export const deleteSupplier = async (supplierId) => {
  const response = await api.delete(`/suppliers/${supplierId}`);
  return response.data;
};

export const addSupplierTransaction = async (supplierId, data) => {
  const response = await api.post(`/suppliers/${supplierId}/transactions`, data);
  return response.data;
};

export const getSupplierTransactions = async (supplierId) => {
  const response = await api.get(`/suppliers/${supplierId}/transactions`);
  return response.data;
};

// ========================================
// Purchase Orders
// ========================================

export const createPurchaseOrder = async (data) => {
  const response = await api.post('/purchase-orders', data);
  return response.data;
};

export const getPurchaseOrders = async (params = {}) => {
  const urlParams = new URLSearchParams();
  if (params.supplierId) urlParams.append('supplierId', params.supplierId);
  if (params.status) urlParams.append('status', params.status);

  const response = await api.get(`/purchase-orders?${urlParams.toString()}`);
  return response.data;
};

export const updatePurchaseOrderStatus = async (orderId, status) => {
  const response = await api.put(`/purchase-orders/${orderId}/status`, { status });
  return response.data;
};

// ========================================
// Modifier Groups
// ========================================

export const getModifierGroups = async (restaurantId) => {
  if (!restaurantId) return { modifierGroups: [] };
  const response = await api.get(`/menu/restaurants/${restaurantId}/modifier-groups`);
  return response.data;
};

export const createModifierGroup = async (restaurantId, data) => {
  const response = await api.post(`/menu/restaurants/${restaurantId}/modifier-groups`, data);
  return response.data;
};

export const updateModifierGroup = async (restaurantId, groupId, data) => {
  const response = await api.put(
    `/menu/restaurants/${restaurantId}/modifier-groups/${groupId}`,
    data
  );
  return response.data;
};

export const deleteModifierGroup = async (restaurantId, groupId) => {
  const response = await api.delete(`/menu/restaurants/${restaurantId}/modifier-groups/${groupId}`);
  return response.data;
};

// ========================================
// Subscriptions
// ========================================

export const getPlans = async () => {
  const response = await api.get('/plans');
  return response.data;
};

export const getPlan = async (planId) => {
  const response = await api.get(`/plans/${planId}`);
  return response.data;
};

export const getSubscriptions = async (params = {}) => {
  const urlParams = new URLSearchParams();
  if (params.organizationId) urlParams.append('organizationId', params.organizationId);
  if (params.status) urlParams.append('status', params.status);

  const response = await api.get(`/subscriptions?${urlParams.toString()}`);
  return response.data;
};

export const getSubscription = async (subscriptionId) => {
  const response = await api.get(`/subscriptions/${subscriptionId}`);
  return response.data;
};

export const createSubscription = async (data) => {
  // Atribución de partner (NER-3079): mergea promoCode/attributionSource del link
  // o cookie si el caller no los seteó. Best-effort; nunca bloquea el alta.
  const payload = mergePromoAttribution(data);
  const response = await api.post('/subscriptions', payload);
  // Consumir la atribución una sola vez, tras un alta exitosa (first-wins).
  if (payload && payload.promoCode) clearPromoAttribution();
  return response.data;
};

export const updateSubscription = async (subscriptionId, data) => {
  const response = await api.put(`/subscriptions/${subscriptionId}`, data);
  return response.data;
};

export const activateMercadoPagoSubscription = async (subscriptionId, payerEmail) => {
  const response = await api.post(`/subscriptions/${subscriptionId}/activate-mp`, { payerEmail });
  return response.data;
};

export const cancelSubscription = async (subscriptionId, immediate = false) => {
  const urlParams = new URLSearchParams();
  if (immediate) urlParams.append('immediate', 'true');

  const response = await api.delete(`/subscriptions/${subscriptionId}?${urlParams.toString()}`);
  return response.data;
};

export const getSubscriptionUsage = async (subscriptionId) => {
  const response = await api.get(`/subscriptions/${subscriptionId}/usage`);
  return response.data;
};

export const getSubscriptionTransactions = async (subscriptionId, params = {}) => {
  const urlParams = new URLSearchParams();
  if (params.limit) urlParams.append('limit', params.limit);
  if (params.lastKey) urlParams.append('lastKey', params.lastKey);

  const queryString = urlParams.toString();
  const url = `/subscriptions/${subscriptionId}/transactions${queryString ? `?${queryString}` : ''}`;

  const response = await api.get(url);
  return response.data;
};

export const createPaymentMethod = async (subscriptionId, data) => {
  const response = await api.post(`/subscriptions/${subscriptionId}/payment-method`, data);
  return response.data;
};

export const getAddons = async (planId) => {
  const response = await api.get('/addons', { params: planId ? { planId } : {} });
  return response.data;
};

export const toggleAddon = async (subscriptionId, addOnId, activate) => {
  const response = await api.post(`/subscriptions/${subscriptionId}/addons`, {
    addOnId,
    action: activate ? 'activate' : 'deactivate',
  });
  return response.data;
};

export const getPlanFeatureMatrix = async () => {
  const response = await api.get('/plans/feature-matrix');
  return response.data;
};

// ========================================
// Delivery Integrations
// ========================================

export const getDeliveryIntegrations = async (restaurantId) => {
  const id = restaurantId || getRestaurantId();
  const response = await api.get(`/delivery/integrations/${id}`);
  return response.data;
};

export const getDeliveryIntegrationConfig = async (restaurantId, platform) => {
  const id = restaurantId || getRestaurantId();
  const response = await api.get(`/delivery/integrations/${id}/${platform}`);
  return response.data;
};

export const updateDeliveryIntegrationConfig = async (restaurantId, platform, data) => {
  const id = restaurantId || getRestaurantId();
  const response = await api.put(`/delivery/integrations/${id}/${platform}`, data);
  return response.data;
};

// ========================================
// Invoicing - Facturación Electrónica AFIP
// ========================================

export const getFiscalConfig = async (restaurantId) => {
  const id = restaurantId || getRestaurantId();
  const response = await api.get(`/invoicing/restaurants/${id}/fiscal-config`);
  return response.data;
};

export const saveFiscalConfig = async (restaurantId, data) => {
  const id = restaurantId || getRestaurantId();
  const response = await api.put(`/invoicing/restaurants/${id}/fiscal-config`, data);
  return response.data;
};

export const generateAfipCSR = async (restaurantId, data = {}) => {
  const id = restaurantId || getRestaurantId();
  const response = await api.post(`/invoicing/restaurants/${id}/generate-csr`, data);
  return response.data;
};

export const deleteAfipCertificate = async (restaurantId) => {
  const id = restaurantId || getRestaurantId();
  const response = await api.delete(`/invoicing/restaurants/${id}/certificate`);
  return response.data;
};

export const uploadAfipCertificate = async (restaurantId, data) => {
  const id = restaurantId || getRestaurantId();
  const response = await api.post(`/invoicing/restaurants/${id}/certificate`, data);
  return response.data;
};

export const getAfipStatus = async (restaurantId) => {
  const id = restaurantId || getRestaurantId();
  const response = await api.get(`/invoicing/restaurants/${id}/afip-status`);
  return response.data;
};

export const createInvoice = async (data) => {
  const response = await api.post('/invoicing/invoices', data);
  return response.data;
};

export const getInvoice = async (invoiceId, restaurantId) => {
  const id = restaurantId || getRestaurantId();
  const response = await api.get(`/invoicing/invoices/${invoiceId}?restaurantId=${id}`);
  return response.data;
};

export const listInvoices = async (restaurantId, params = {}) => {
  const id = restaurantId || getRestaurantId();
  const query = new URLSearchParams(params).toString();
  const response = await api.get(`/invoicing/restaurants/${id}/invoices?${query}`);
  return response.data;
};

export const createCreditNote = async (data) => {
  const response = await api.post('/invoicing/credit-notes', data);
  return response.data;
};

export const createDebitNote = async (data) => {
  const response = await api.post('/invoicing/debit-notes', data);
  return response.data;
};

export const getInvoicePdf = async (invoiceId, restaurantId) => {
  const id = restaurantId || getRestaurantId();
  const response = await api.get(`/invoicing/invoices/${invoiceId}/pdf?restaurantId=${id}`);
  return response.data;
};

// ==================== Custom Domains ====================

export const listCustomDomains = async () => {
  const response = await api.get('/custom-domains');
  return response.data;
};

export const registerCustomDomain = async (hostname, type = 'menu') => {
  const response = await api.post('/custom-domains', { hostname, type });
  return response.data;
};

export const removeCustomDomain = async (hostname) => {
  const response = await api.delete(`/custom-domains/${encodeURIComponent(hostname)}`);
  return response.data;
};

export const verifyCustomDomain = async (hostname) => {
  const response = await api.post(`/custom-domains/${encodeURIComponent(hostname)}/verify`);
  return response.data;
};

// MercadoPago
export const testMercadoPagoToken = async (accessToken) => {
  const response = await api.post('/payments/mercadopago/test', { accessToken });
  return response.data;
};

// Chat / Support Messages
export const getChatMessages = async () => {
  const restaurantId = getRestaurantId();
  const response = await api.get('/chat/messages', { params: { restaurantId } });
  return response.data;
};

export const sendChatMessage = async (message) => {
  const restaurantId = getRestaurantId();
  const response = await api.post('/chat/messages', { message }, { params: { restaurantId } });
  return response.data;
};

export const markChatMessagesRead = async () => {
  const restaurantId = getRestaurantId();
  const response = await api.post('/chat/messages/read', {}, { params: { restaurantId } });
  return response.data;
};

// Promotion Rules (NER-4485)
// Backend real: services/menu -> `/menu/restaurants/{rid}/promo-rules` (GET/POST)
// y `.../promo-rules/{ruleId}` (PUT/DELETE). Id = `ruleId`, estado = `status`.
// NO existe PATCH: el toggle se hace con PUT reenviando el body completo con el
// `status` invertido (ver ruleToUpdateBody en utils/promoMapper).
export const getPromotionRules = async () => {
  const restaurantId = getRestaurantId();
  const response = await api.get(`/menu/restaurants/${restaurantId}/promo-rules`);
  return response.data;
};

export const createPromotionRule = async (data) => {
  const restaurantId = getRestaurantId();
  const response = await api.post(`/menu/restaurants/${restaurantId}/promo-rules`, data);
  return response.data;
};

export const updatePromotionRule = async (ruleId, data) => {
  const restaurantId = getRestaurantId();
  const response = await api.put(`/menu/restaurants/${restaurantId}/promo-rules/${ruleId}`, data);
  return response.data;
};

export const deletePromotionRule = async (ruleId) => {
  const restaurantId = getRestaurantId();
  const response = await api.delete(`/menu/restaurants/${restaurantId}/promo-rules/${ruleId}`);
  return response.data;
};

// Toggle activo/inactivo. El backend no tiene PATCH: reenviamos el body anidado
// completo (name/type/schedule/discount/targets) con el `status` ya invertido.
export const togglePromotionRule = async (ruleId, ruleBody) => {
  const restaurantId = getRestaurantId();
  const response = await api.put(
    `/menu/restaurants/${restaurantId}/promo-rules/${ruleId}`,
    ruleBody
  );
  return response.data;
};

// Enterprise Leads
export const submitEnterpriseLead = async (data) => {
  const response = await api.post('/enterprise-leads', data);
  return response.data;
};

// Partners / promo codes (programa influencers NER-3079). OWNER-only (RBAC `partners`).
// El identificador del partner es su `code` (inmutable). No hay DELETE: se da de baja
// vía PATCH status: 'terminated'.
export const getPartners = async () => {
  const response = await api.get('/admin/partners');
  return response.data;
};

export const getPartner = async (code) => {
  const response = await api.get(`/admin/partners/${encodeURIComponent(code)}`);
  return response.data;
};

export const createPartner = async (data) => {
  const response = await api.post('/admin/partners', data);
  return response.data;
};

export const updatePartner = async (code, data) => {
  const response = await api.patch(`/admin/partners/${encodeURIComponent(code)}`, data);
  return response.data;
};

// RES-88: el panel de partners es interno de Nerdo. `{ allowed }` (siempre 200).
export const getPartnerAccess = async () => {
  const response = await api.get('/admin/partners/access');
  return response.data;
};

// RES-91: cálculo de comisiones de un período ('YYYY-MM') y CSV de payouts
export const calculatePartnerCommissions = async (period) => {
  const response = await api.post('/admin/partners/calculate', period ? { period } : {});
  return response.data;
};

// Devuelve el CSV como texto (el caller arma la descarga)
export const getPartnerPayoutsCsv = async (period) => {
  const response = await api.get('/admin/partners/payouts.csv', {
    params: period ? { period } : undefined,
    responseType: 'text',
    transformResponse: [(data) => data],
  });
  return response.data;
};

// RES-93: invitar al portal a un partner con código ya creado
export const invitePartnerPortal = async (code) => {
  const response = await api.post(`/admin/partners/${encodeURIComponent(code)}/portal-invite`);
  return response.data;
};

// RES-94: solicitudes para ser partner
export const getPartnerApplications = async (status) => {
  const response = await api.get('/admin/partners/applications', {
    params: status ? { status } : undefined,
  });
  return response.data;
};

// data: { decision: 'approve', code?, tier?, partnerName?, commissionPct? } | { decision: 'reject', rejectionReason }
export const reviewPartnerApplication = async (applicationId, data) => {
  const response = await api.post(
    `/admin/partners/applications/${encodeURIComponent(applicationId)}/review`,
    data
  );
  return response.data;
};

// Públicos (sin login): formulario de solicitud (RES-94) y datos del código para el alta (RES-99)
export const createPartnerApplication = async (data) => {
  const response = await api.post('/partners/applications', data);
  return response.data;
};

export const getPublicPartnerCode = async (code) => {
  const response = await api.get(`/partners/codes/${encodeURIComponent(code)}`);
  return response.data;
};

// Portal del partner (rol PARTNER, RES-93)
export const getMyPartner = async () => {
  const response = await api.get('/partners/me');
  return response.data;
};

// data: { holderName, cuit, cbuOrAlias, taxCondition: 'monotributo' | 'responsable_inscripto' }
export const updateMyPayoutDetails = async (data) => {
  const response = await api.put('/partners/me/payout-details', data);
  return response.data;
};

// ─── Plantillas propias a nivel organización (NER-4488 fase 3) ──────────────
// Backend: services/menu → /menu/organizations/{organizationId}/templates.
// kind: 'combo' | 'modifierGroup' | 'promoRule'. payload lo define el admin.
// El org del path debe coincidir con el header X-Organization-Id (selectedOrganizationId).
export const getOrgTemplates = async (kind) => {
  const organizationId = storage.get('selectedOrganizationId') || getOrganizationId();
  if (!organizationId) return { templates: [] };
  const response = await api.get(`/menu/organizations/${organizationId}/templates`, {
    params: kind ? { kind } : undefined,
  });
  return response.data;
};

export const createOrgTemplate = async (data) => {
  const organizationId = storage.get('selectedOrganizationId') || getOrganizationId();
  const response = await api.post(`/menu/organizations/${organizationId}/templates`, data);
  return response.data;
};

export const updateOrgTemplate = async (templateId, data) => {
  const organizationId = storage.get('selectedOrganizationId') || getOrganizationId();
  const response = await api.put(
    `/menu/organizations/${organizationId}/templates/${templateId}`,
    data
  );
  return response.data;
};

export const deleteOrgTemplate = async (templateId) => {
  const organizationId = storage.get('selectedOrganizationId') || getOrganizationId();
  const response = await api.delete(
    `/menu/organizations/${organizationId}/templates/${templateId}`
  );
  return response.data;
};

export default api;
